/**
 * AccountsFilter Component
 * TASK-207: Portfolio Level View - Enhanced Filters
 *
 * Multi-select dropdown for filtering by accounts
 * Shows account name, type, and currency with checkbox selection
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { AccountFilter } from '../../../types/portfolio-views';

interface AccountsFilterProps {
  accounts: AccountFilter[];
  onChange: (accounts: AccountFilter[]) => void;
  disabled?: boolean;
}

const AccountsFilter: React.FC<AccountsFilterProps> = ({
  accounts,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter accounts based on search
  const filteredAccounts = searchQuery
    ? accounts.filter((acc) =>
        acc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        acc.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        acc.currency.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : accounts;

  // Count enabled accounts
  const enabledCount = accounts.filter((acc) => acc.enabled).length;
  const allEnabled = enabledCount === accounts.length;

  // Toggle individual account
  const handleToggleAccount = (accountId: string) => {
    const updated = accounts.map((acc) =>
      acc.id === accountId ? { ...acc, enabled: !acc.enabled } : acc
    );
    onChange(updated);
  };

  // Toggle all accounts
  const handleToggleAll = () => {
    const newState = !allEnabled;
    const updated = accounts.map((acc) => ({ ...acc, enabled: newState }));
    onChange(updated);
  };

  // Get display text
  const getDisplayText = () => {
    if (allEnabled) {
      return `All Accounts (${accounts.length})`;
    }
    if (enabledCount === 0) {
      return 'No accounts selected';
    }
    if (enabledCount === 1) {
      const enabled = accounts.find((acc) => acc.enabled);
      return enabled ? enabled.name : '1 account';
    }
    return `${enabledCount} of ${accounts.length} accounts`;
  };

  // Account type badge colors
  const getAccountTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      investment: 'bg-blue-100 text-blue-800',
      cash: 'bg-green-100 text-green-800',
      custody: 'bg-purple-100 text-purple-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Accounts
      </label>

      <div ref={dropdownRef} className="relative">
        {/* Dropdown Trigger */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            w-full px-3 py-2 border rounded-md shadow-sm
            flex items-center justify-between
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer hover:bg-gray-50'}
            border-gray-300
          `}
        >
          <span className={`text-sm truncate ${enabledCount === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
            {getDisplayText()}
          </span>
          <ChevronDown
            className={`w-5 h-5 text-gray-400 transition-transform ml-2 flex-shrink-0 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
            {/* Search */}
            {accounts.length > 3 && (
              <div className="p-2 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search accounts..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {/* Select All Option */}
            <div className="border-b border-gray-200">
              <label
                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={allEnabled}
                  onChange={handleToggleAll}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm font-medium text-gray-900">
                  Select All ({accounts.length})
                </span>
              </label>
            </div>

            {/* Account List */}
            <div className="overflow-y-auto">
              {filteredAccounts.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  No accounts found
                </div>
              ) : (
                filteredAccounts.map((account) => (
                  <label
                    key={account.id}
                    className="flex items-start px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={account.enabled}
                      onChange={() => handleToggleAccount(account.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                    />
                    <div className="ml-3 flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {account.name}
                        </span>
                        {account.enabled && (
                          <Check className="w-4 h-4 text-blue-600 flex-shrink-0 ml-2" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getAccountTypeBadge(
                            account.type
                          )}`}
                        >
                          {account.type}
                        </span>
                        <span className="text-xs text-gray-500">
                          {account.currency}
                        </span>
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Help text */}
      <p className="text-xs text-gray-500">
        {allEnabled
          ? 'All accounts included'
          : enabledCount === 0
          ? 'Select at least one account'
          : `${accounts.length - enabledCount} ${accounts.length - enabledCount === 1 ? 'account' : 'accounts'} hidden`}
      </p>
    </div>
  );
};

export default AccountsFilter;
